import { LegalLayout } from './LegalLayout';

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="2026-04-22">
      <p className="lead">
        These Terms of Service ("Terms") govern your use of the Praxara platform
        ("Service"), operated by Praxara Ltd ("Praxara", "we", "us"). By accessing
        or using the Service you ("Customer") agree to these Terms.
      </p>

      <h2>1. Service description</h2>
      <p>
        Praxara provides GxP-ready AI for pharmacovigilance, regulatory, and
        compliance. Three modules on one tamper-evident audit chain. The
        Service includes
        document ingestion, AI-assisted extraction, human review, electronic
        signatures compliant with 21 CFR Part 11 expectations, and a tamper-
        evident audit trail.
      </p>

      <h2>2. Accounts + access</h2>
      <p>
        Customer is responsible for safeguarding account credentials and for all
        activity occurring under its account. Customer must notify Praxara
        immediately of any unauthorised access. Multi-factor authentication is
        strongly recommended and may be enforced by tenant policy.
      </p>

      <h2>3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Upload content that you do not have the right to upload, including patient-identifying data not properly pseudonymised.</li>
        <li>Use the Service to generate regulatory submissions without qualified human review and approval.</li>
        <li>Attempt to probe, scan or disable security mechanisms of the Service.</li>
        <li>Reverse-engineer the underlying AI models, prompts, or skill implementations.</li>
      </ul>

      <h2>4. AI outputs + human oversight</h2>
      <p>
        AI-generated outputs are <strong>decision support, not final decisions</strong>.
        Customer retains full responsibility for regulatory submissions and clinical
        determinations made on the basis of Service output. All outputs intended for
        regulatory use must be reviewed and approved by a qualified professional
        via the Service's e-signature workflow before export.
      </p>
      <p>
        Under the EU AI Act, Praxara classifies its system as high-risk in
        regulatory contexts and maintains records, model cards, and a risk
        register available on request. Customer is the "deployer" under Article 26
        of the EU AI Act and must ensure human oversight as required.
      </p>

      <h2>5. Data ownership + processing</h2>
      <p>
        Customer retains all intellectual property rights in data uploaded to the
        Service. Praxara processes such data solely as Customer's data processor
        under the <a href="/legal/dpa">Data Processing Agreement</a>. Praxara does
        not use Customer data to train general-purpose AI models.
      </p>

      <h2>6. Fees + billing</h2>
      <p>
        Subscription fees are billed in advance per the plan selected. Usage-based
        overages (pages processed beyond the plan quota) are billed in arrears.
        All prices are exclusive of VAT unless stated. Stripe is used as the
        payment processor.
      </p>

      <h2>7. Service levels</h2>
      <p>
        Praxara targets 99.5% monthly uptime for the Professional plan and 99.9%
        for Enterprise. Scheduled maintenance is announced at least 48 hours in
        advance and excluded from uptime calculations. Service credits and
        remedies are specified in the plan agreement.
      </p>

      <h2>8. Termination</h2>
      <p>
        Either party may terminate this agreement with 30 days' written notice.
        Upon termination Customer may export all data via the provided API for
        up to 30 days, after which Praxara will permanently delete Customer data
        unless legally required to retain it.
      </p>

      <h2>9. Warranties + liability</h2>
      <p>
        The Service is provided "as is". Praxara disclaims all implied warranties
        to the maximum extent permitted by law. Total liability is capped at the
        fees paid by Customer in the 12 months preceding the claim. Praxara is
        not liable for indirect, incidental, or consequential damages. This
        clause does not limit liability for fraud, gross negligence, death or
        personal injury.
      </p>

      <h2>10. Governing law</h2>
      <p>
        These Terms are governed by the laws of England and Wales. Disputes
        are subject to the exclusive jurisdiction of the English courts.
      </p>

      <h2>11. Changes to these Terms</h2>
      <p>
        Material changes will be notified via email at least 30 days before
        taking effect. Continued use of the Service after that period
        constitutes acceptance.
      </p>

      <p className="mt-8 text-sm text-muted-foreground">
        Praxara Ltd is a company registered in England and Wales.
        Contact: <a href="mailto:legal@praxara.io">legal@praxara.io</a>.
      </p>
    </LegalLayout>
  );
}
